import { Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useCredits } from "@/hooks/use-user";
import { Button } from "@/components/ui/button";
import {
  Eye,
  Image,
  Megaphone,
  Gift,
  Tv,
  Package,
  Coins,
  ArrowRight,
  Sparkles,
  Zap,
  Star,
  Globe,
  TrendingUp,
  CreditCard,
} from "lucide-react";

const modules = [
  {
    title: "Virtual Try-On",
    description: "Instantly visualize apparel on AI-powered models with photorealistic accuracy.",
    path: "/app/try-on",
    icon: Eye,
    badge: "Most Popular",
    badgeColor: "bg-primary text-primary-foreground",
  },
  {
    title: "Product Staging",
    description: "Generate professional lifestyle and studio photos in seconds.",
    path: "/app/staging",
    icon: Image,
    badge: null,
  },
  {
    title: "Ad Generator",
    description: "Create high-converting ad creatives at scale with AI automation.",
    path: "/app/ads",
    icon: Megaphone,
    badge: "Hot",
    badgeColor: "bg-orange-500 text-white",
  },
  {
    title: "Gift Designer",
    description: "Design personalized gift visualizations instantly for any occasion.",
    path: "/app/designer",
    icon: Gift,
    badge: null,
  },
  {
    title: "AR Visualizer",
    description: "Augmented reality product placement for furniture and décor.",
    path: "/app/visualizer",
    icon: Tv,
    badge: null,
  },
  {
    title: "Unboxing Videos",
    description: "AI-generated unboxing video content at scale — no camera needed.",
    path: "/app/unboxing",
    icon: Package,
    badge: "New",
    badgeColor: "bg-emerald-600 text-white",
  },
];

const DashboardHome = () => {
  const { user } = useUser();
  const { credits, subscriptionTier } = useCredits();

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName =
    user?.firstName ||
    user?.emailAddresses[0]?.emailAddress?.split("@")[0] ||
    "Creator";

  const stats = [
    { label: "AI Tools", value: "6", icon: Zap },
    { label: "Credits", value: credits.toLocaleString(), icon: Coins },
    { label: "Plan", value: subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1), icon: Star },
    { label: "Markets", value: "150+", icon: Globe },
  ];

  return (
    <div className="p-6 md:p-10 space-y-10 min-h-screen">
      {/* ── Welcome Header ── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-widest">
              AI-Powered Creative Suite
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            {greeting},{" "}
            <span className="text-primary">{firstName}</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-base">
            Ready to create something extraordinary today?
          </p>
        </div>

        {/* Quick stat pills */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="bg-card border border-primary/40 rounded-xl px-5 py-3 text-center min-w-[120px]">
            <div className="flex items-center justify-center gap-2 text-primary mb-1">
              <Coins className="w-4 h-4" />
              <span className="text-xl font-bold">{credits.toLocaleString()}</span>
            </div>
            <p className="text-xs text-muted-foreground">Credits</p>
          </div>
          <div className="bg-card border border-border rounded-xl px-5 py-3 text-center min-w-[120px]">
            <div className="flex items-center justify-center gap-2 text-foreground mb-1">
              <Star className="w-4 h-4 text-primary" />
              <span className="text-sm font-bold capitalize">{subscriptionTier}</span>
            </div>
            <p className="text-xs text-muted-foreground">Current Plan</p>
          </div>
        </div>
      </div>

      {/* ── Stats Row ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors"
          >
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── AI Tools Grid ── */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            AI Creative Tools
          </h2>
          <span className="text-xs text-muted-foreground">{modules.length} tools available</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {modules.map((mod) => {
            const Icon = mod.icon;
            return (
              <Link
                key={mod.path}
                to={mod.path}
                className="group relative bg-card border border-border hover:border-primary/60 rounded-xl p-6 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-0.5"
              >
                {/* Badge */}
                {mod.badge && (
                  <span
                    className={`absolute top-4 right-4 text-[11px] px-2 py-0.5 rounded-full font-semibold ${mod.badgeColor}`}
                  >
                    {mod.badge}
                  </span>
                )}

                {/* Icon */}
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-6 h-6 text-primary" />
                </div>

                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2 text-base">
                  {mod.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {mod.description}
                </p>

                <div className="flex items-center gap-1 text-primary text-sm font-medium">
                  Launch Tool
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Quick Actions / Upgrade CTA ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Performance tip */}
        <div className="bg-card border border-border rounded-xl p-6 flex gap-4">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-1">Maximize Your Output</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Batch-process multiple products simultaneously with our AI pipeline for
              maximum throughput and efficiency.
            </p>
          </div>
        </div>

        {/* Upgrade or billing */}
        {subscriptionTier === "free" ? (
          <div className="bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/40 rounded-xl p-6 flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-foreground mb-1">Unlock Full Power</h3>
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro for unlimited credits and premium features.
              </p>
            </div>
            <Button asChild size="sm" className="flex-shrink-0">
              <Link to="/pricing">
                Upgrade
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Link>
            </Button>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl p-6 flex gap-4">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-1">Billing &amp; Credits</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Manage your subscription and purchase additional credits.
              </p>
              <Button variant="secondary" size="sm" asChild>
                <Link to="/app/billing">View Billing</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
